import type { ManageAPI } from "~/manage-api/types";

/**
 * Get current day index (0-6, where 0 is Monday)
 * Returns the current day of the week as an index where Monday = 0, Sunday = 6
 */
export function getCurrentDayIndex(): number {
  const today = new Date();
  let dayIndex = today.getDay() - 1; // getDay() returns 0-6 where 0 is Sunday
  return dayIndex < 0 ? 6 : dayIndex; // Handle Sunday case
}

/**
 * Parse lunch time string and extract times for different days
 * Handles various formats like "11-14", "Weekdays 10-14, Sat 11-15", etc.
 */
export function parseLunchTime(lunchTimeStr: string, selectedDayIndex: number): string {
  if (!lunchTimeStr) return '';
  
  // Simple case: if it's just a time like "11-14", return as is
  if (!lunchTimeStr.includes(',') && !lunchTimeStr.toLowerCase().includes('weekday') &&
      !lunchTimeStr.toLowerCase().includes('sat') && !lunchTimeStr.toLowerCase().includes('sun')) {
    return lunchTimeStr.trim();
  }

  try {
    const parts = lunchTimeStr.split(',').map(p => p.trim());
    const times = {
      weekday: '',
      saturday: '',
      sunday: ''
    };
    
    parts.forEach(part => {
      if (part.toLowerCase().includes('weekday')) {
        times.weekday = part.replace(/weekdays?/i, '').trim();
      } else if (part.toLowerCase().includes('sat')) {
        times.saturday = part.replace(/sat\.?/i, '').trim();
      } else if (part.toLowerCase().includes('sun')) {
        times.sunday = part.replace(/sun\.?/i, '').trim();
      } else if (parts.length === 1) {
        // If there's only one part and it doesn't match any pattern, use it for all days
        times.weekday = part;
        times.saturday = part;
        times.sunday = part;
      }
    });

    // Return appropriate time for selected day
    if (selectedDayIndex === 5) return times.saturday; // Saturday
    if (selectedDayIndex === 6) return times.sunday;   // Sunday
    return times.weekday;                              // Monday-Friday
  } catch (e) {
    return lunchTimeStr;
  }
}

/**
 * Parse daily menus and get menu items for a specific day
 * Returns an array of menu items for the given day index
 */
export function getMenuForDay(lunchItem: ManageAPI.Lunch, dayIndex: number): string[] {
  if (!lunchItem?.dailyLunchMenus) return [];
  
  if (lunchItem.sameMenuEachDay) {
    // If same menu each day, return the first menu
    const menu = lunchItem.dailyLunchMenus[0];
    return menu?.lunchMenu ? menu.lunchMenu.split('\r\n').filter(item => item.trim()) : [];
  }
  
  // Find menu for specific day
  const menu = lunchItem.dailyLunchMenus.find(m => {
    try {
      if (!m.day || !m.day.includes('.')) return false;
      const [day, month, year] = m.day.split('.').map(Number);
      if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
      const menuDate = new Date(year, month - 1, day);
      const menuDayIndex = menuDate.getDay() - 1;
      return menuDayIndex === dayIndex || (dayIndex === 6 && menuDayIndex === -1); // Handle Sunday case
    } catch (e) {
      return false;
    }
  });
  
  return menu?.lunchMenu ? menu.lunchMenu.split('\r\n').filter(item => item.trim()) : [];
}

/**
 * Check if a day has any lunch menus available
 * Returns true if any restaurant has lunch menus for the given day
 */
export function isDayAvailable(
  dayIndex: number, 
  storesWithLunchData: Array<{ store: ManageAPI.Store; lunch: ManageAPI.Lunch | undefined }>
): boolean {
  return storesWithLunchData.some(item => {
    if (!item.lunch) return false;
    
    if (item.lunch.sameMenuEachDay) return true;
    
    return item.lunch.dailyLunchMenus.some(menu => {
      try {
        if (!menu.day || !menu.day.includes('.')) return false;
        const [day, month, year] = menu.day.split('.').map(Number);
        if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
        const menuDate = new Date(year, month - 1, day);
        const menuDayIndex = menuDate.getDay() - 1;
        return menuDayIndex === dayIndex || (dayIndex === 6 && menuDayIndex === -1);
      } catch (e) {
        return false;
      }
    });
  });
}

/**
 * Get lunch time for a specific day from parsed lunch times
 * Used for checking if a day has lunch time available
 */
export function getLunchTimeForDay(dayIndex: number, times: { weekday: string; saturday: string; sunday: string }): string {
  if (dayIndex < 0 || dayIndex > 6) {
    return "";
  }
  
  if (dayIndex === 5) return times.saturday; // Saturday
  if (dayIndex === 6) return times.sunday;   // Sunday
  return times.weekday;                      // Monday-Friday
}

/**
 * Parse lunch time string into structured format
 * Returns an object with weekday, saturday, and sunday times
 */
export function parseLunchTimeStructured(lunchTimeStr: string): { weekday: string; saturday: string; sunday: string } {
  const times = {
    weekday: '',
    saturday: '',
    sunday: ''
  };
  
  if (!lunchTimeStr) {
    return times;
  }

  try {
    // Simple case: if it's just a time like "11-14", apply it to all days
    if (!lunchTimeStr.includes(',') && !lunchTimeStr.toLowerCase().includes('weekday') &&
        !lunchTimeStr.toLowerCase().includes('sat') && !lunchTimeStr.toLowerCase().includes('sun')) {
      times.weekday = lunchTimeStr.trim();
      times.saturday = lunchTimeStr.trim();
      times.sunday = lunchTimeStr.trim();
      return times;
    }

    const parts = lunchTimeStr.split(',').map(p => p.trim());
    parts.forEach(part => {
      if (part.toLowerCase().includes('weekday')) {
        times.weekday = part.replace(/weekdays?/i, '').trim();
      } else if (part.toLowerCase().includes('sat')) {
        times.saturday = part.replace(/sat\.?/i, '').trim();
      } else if (part.toLowerCase().includes('sun')) {
        times.sunday = part.replace(/sun\.?/i, '').trim();
      } else if (parts.length === 1) {
        // If there's only one part and it doesn't match any pattern, use it for all days
        times.weekday = part;
        times.saturday = part;
        times.sunday = part;
      }
    });

  } catch (e) {
    // Return empty times on error
  }

  return times;
}

/**
 * Check if a day has lunch time available
 * Returns true if the day has lunch time or if there are any lunch menus available
 */
export function hasLunchTime(
  dayIndex: number, 
  times: { weekday: string; saturday: string; sunday: string },
  dailyMenus: any[]
): boolean {
  if (dayIndex < 0 || dayIndex > 6) {
    return false;
  }
  
  // Weekdays with any lunch menu should be enabled
  if (dailyMenus.length > 0 && dayIndex >= 0 && dayIndex <= 4) {
    return true;
  }
  
  if (dayIndex === 5) {
    return !!times.saturday;
  }
  if (dayIndex === 6) {
    return !!times.sunday;
  }
  
  // Return true for weekdays to enable them as a fallback 
  // when there's any lunch menu available
  return !!times.weekday || (dailyMenus.length > 0);
}

/**
 * Create a map of stores with their lunch data
 * Filters stores that have lunch menus and matches them with lunch data
 */
export function createStoresWithLunchData(
  stores: ManageAPI.Store[],
  lunchData: ManageAPI.Lunch[]
): Array<{ store: ManageAPI.Store; lunch: ManageAPI.Lunch | undefined }> {
  const storesWithLunch = stores?.filter((store: ManageAPI.Store) => store.hasLunchMenu) || [];
  
  return storesWithLunch.map(store => {
    const lunchItem = lunchData.find((l: ManageAPI.Lunch) => l.store?.id === store.id);
    return {
      store,
      lunch: lunchItem
    };
  }).filter(item => item.lunch); // Only include stores that actually have lunch data
}
